import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { container } from "tsyringe";
import { subscriptionDocumentValidation } from "common/utils/subscriptionDocumentValidation";
import { isPrivateEquityOnly } from "common/utils/privateEquity";
import SubscriptionService from "services/SubscriptionService";
import ModuleConfig from "configs/ModuleConfig";
import Modal from "components/elements/Modal";
import Typography, { ITypo } from "components/elements/Typography";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import classes from "./documentFinalization.module.scss";

const service = container.resolve(SubscriptionService);
const modules = container.resolve(ModuleConfig).get().modules;
type Review = {
	mode: "pending" | "automatic" | "reported" | "error" | "investor_editing";
	pendingNames: string[];
	pendingPath?: string;
	withoutRto?: boolean;
	investorEditable?: boolean;
};
type Step = { name: string; path: string };

/** Shared by physical and legal persons; re-read persisted statuses before sending. */
export default function useDocumentFinalization(subscriptionId: string, steps: Step[], corporation = false) {
	const navigate = useNavigate();
	const [review, setReview] = useState<Review | null>(null);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const locked = useRef(false);
	const close = () => {
		if (!locked.current) {
			setReview(null);
			setError("");
		}
	};

	const readReview = useCallback(async (): Promise<Review> => {
		const subscription = await service.getByUid(subscriptionId);
		if (!subscription) throw new Error("Subscription unavailable");
		if (subscription.investorInvitation?.hasPendingAmendments) return { mode: "investor_editing", pendingNames: [] };
		const validation = subscriptionDocumentValidation(subscription);
		const withoutRto = isPrivateEquityOnly(subscription.productsSubscribed);
		if (validation.hasAnomaly) return { mode: "reported", pendingNames: [], withoutRto };
		if (validation.allValidated) return { mode: "automatic", pendingNames: [], withoutRto, investorEditable: !!subscription.investorInvitation };
		// Une étape sans identifiant de produit dans son chemin est la convention RTO (absente en private equity).
		const productIds = subscription.productsSubscribed?.map((product) => product.id) ?? [];
		const pendingSteps = steps.filter((step) => {
			const productId = productIds.find((id) => step.path.includes(id));
			return productId ? validation.pendingProductIds.includes(productId) : validation.pendingRto;
		});
		return {
			mode: "pending",
			pendingNames: pendingSteps.map((step) => step.name),
			pendingPath: pendingSteps[0]?.path,
		};
	}, [subscriptionId, steps]);

	const reviewDocuments = useCallback(async () => {
		if (locked.current) return;
		locked.current = true;
		setBusy(true);
		setError("");
		try {
			setReview(await readReview());
		} catch {
			setReview({ mode: "error", pendingNames: [] });
			setError("Impossible de vérifier les documents. Veuillez réessayer.");
		} finally {
			locked.current = false;
			setBusy(false);
		}
	}, [readReview]);

	const send = async () => {
		if (locked.current || !review || !["automatic", "reported"].includes(review.mode)) return;
		locked.current = true;
		setBusy(true);
		setError("");
		try {
			const current = await readReview();
			setReview(current);
			// A changed document state requires a new review and explicit confirmation.
			if (current.mode !== review.mode) return;
			if (corporation) await service.submitCorporation(subscriptionId);
			else await service.submit(subscriptionId);
			navigate(modules.pages.Subscriptions.props.pages.WaitingSignature.props.path);
		} catch {
			setError("La souscription n'a pas pu être finalisée. Vérifiez les documents, puis réessayez.");
		} finally {
			locked.current = false;
			setBusy(false);
		}
	};

	const dialog = (
		<Modal isOpen={review !== null} onClose={close} className={classes["modal"]}>
			<Typography typo={ITypo.H1}>
				{review?.mode === "investor_editing"
					? "Dossier en cours de modification"
					: review?.mode === "pending"
						? "Documents à valider"
						: review?.mode === "reported"
							? "Confirmer le signalement"
							: review?.mode === "error"
								? "Vérification des documents"
								: "Confirmer et envoyer en e-signature"}
			</Typography>
			{review?.mode === "investor_editing" && (
				<Typography typo={ITypo.P_MEDIUM}>
					L’investisseur modifie son dossier. Attendez son nouvel envoi, puis relisez les réponses et les documents avant de valider.
				</Typography>
			)}
			{review?.investorEditable && <Typography typo={ITypo.P_MEDIUM}>La validation du dossier met fin aux modifications depuis le portail investisseur.</Typography>}
			{review?.mode === "pending" && (
				<>
					<Typography typo={ITypo.P_MEDIUM}>Avant l'envoi en signature électronique, veuillez vérifier et valider les documents suivants :</Typography>
					<ul>
						{review.pendingNames.map((name, index) => (
							<li key={index}>{name}</li>
						))}
					</ul>
					<Typography typo={ITypo.P_MEDIUM}>Votre dossier reste en brouillon. Aucun envoi en signature n'a été déclenché.</Typography>
				</>
			)}
			{review?.mode === "automatic" && (
				<Typography typo={ITypo.P_MEDIUM}>
					{review.withoutRto ? (
						"Les documents de souscription seront envoyés dans une même liasse de signature électronique."
					) : (
						<>
							Pour les dossiers entièrement numériques, la convention RTO et les bulletins seront envoyés dans une même liasse de signature électronique.
							<br />
							<br />
							La RTO figurera en premier. Une RTO déjà signée séparément ne sera pas redemandée.
						</>
					)}
					<br />
					<br />
					Vous recevrez un email pour transmettre les dossiers aux sociétés de gestion et serez informé à chaque étape du processus par email.
				</Typography>
			)}
			{review?.mode === "reported" && (
				<Typography typo={ITypo.P_MEDIUM}>
					En confirmant, vous validez l’envoi du signalement.
					<br />
					<br />
					Le dossier sera transmis au back-office de Wenimmo, qui analysera l’erreur et procédera à l’envoi du dossier en signature électronique à votre/vos client(s)
					dans les meilleurs délais.
					<br />
					<br />
					Le back-office de Wenimmo est susceptible de revenir vers vous si des informations complémentaires sont nécessaires.
				</Typography>
			)}
			{error && <MessageBox type="warning" text={error} />}
			<div className={classes["buttons"]}>
				<Button disabled={busy} onClick={close} variant={EButtonVariant.OUTLINED}>
					Fermer
				</Button>
				{review?.mode === "pending" && review.pendingPath && (
					<Button
						disabled={busy}
						onClick={() => {
							close();
							navigate(review.pendingPath!);
						}}>
						Revenir aux documents à valider
					</Button>
				)}
				{review?.mode === "error" && (
					<Button disabled={busy} onClick={reviewDocuments}>
						Réessayer
					</Button>
				)}
				{(review?.mode === "automatic" || review?.mode === "reported") && (
					<Button disabled={busy} onClick={send}>
						{busy ? "Envoi en cours…" : "Confirmer"}
					</Button>
				)}
			</div>
		</Modal>
	);
	return { reviewDocuments, dialog, busy };
}
